/**
 * Google Analytics 4 helper — gated behind cookie consent.
 *
 * Reads REACT_APP_GA4_MEASUREMENT_ID from env. If empty, all calls are no-ops.
 * initGA4() injects gtag.js once and configures the measurement.
 * Page views are fired manually on route change so we control SPA tracking.
 * E-commerce events follow the GA4 recommended schema:
 *   view_item, add_to_cart, begin_checkout, purchase
 */
const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

let _state = { ready: false, injected: false };

export const isGA4Configured = () => Boolean(MEASUREMENT_ID);

export const initGA4 = () => {
  if (_state.ready || _state.injected) return;
  if (!MEASUREMENT_ID) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  _state.injected = true;
  const s = document.createElement("script");
  s.async = true;
  s.id = "ga4-loader";
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line func-names
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // We fire page_view manually on each route change so disable auto.
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });
  _state.ready = true;
};

const _safe = (fn) => {
  try { fn(); } catch (e) { /* no-op */ }
};

export const ga4PageView = (pathname) => {
  if (!_state.ready || typeof window === "undefined" || !window.gtag) return;
  _safe(() => {
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: pathname || window.location.pathname,
      page_title: document.title,
    });
  });
};

const _itemFromGem = (gem, valueUsd) => ({
  item_id: gem?.id,
  item_name: gem?.name,
  item_category: gem?.category || "Gemstone",
  price: Number(valueUsd ?? gem?.reserve_price ?? 0),
  quantity: 1,
});

export const ga4ViewItem = (gem, valueUsd) => {
  if (!_state.ready || !window.gtag || !gem) return;
  _safe(() => {
    window.gtag("event", "view_item", {
      currency: "USD",
      value: Number(valueUsd ?? gem.reserve_price ?? 0),
      items: [_itemFromGem(gem, valueUsd)],
    });
  });
};

export const ga4AddToCart = (gem, valueUsd) => {
  if (!_state.ready || !window.gtag || !gem) return;
  _safe(() => {
    window.gtag("event", "add_to_cart", {
      currency: "USD",
      value: Number(valueUsd ?? gem.reserve_price ?? 0),
      items: [_itemFromGem(gem, valueUsd)],
    });
  });
};

export const ga4BeginCheckout = (gem, valueUsd, method) => {
  if (!_state.ready || !window.gtag || !gem) return;
  _safe(() => {
    window.gtag("event", "begin_checkout", {
      currency: "USD",
      value: Number(valueUsd ?? gem.reserve_price ?? 0),
      items: [_itemFromGem(gem, valueUsd)],
      payment_method: method || "unknown",
    });
  });
};

export const ga4Purchase = ({ gemId, gemName, value, currency = "USD", orderId }) => {
  if (!_state.ready || !window.gtag) return;
  _safe(() => {
    window.gtag("event", "purchase", {
      transaction_id: orderId,
      currency,
      value: Number(value || 0),
      items: gemId ? [{ item_id: gemId, item_name: gemName, quantity: 1, price: Number(value || 0) }] : [],
    });
  });
};

// Generic event helper for non-ecommerce signals (e.g. marketplace clicks).
export const ga4Event = (name, params = {}) => {
  if (!_state.ready || !window.gtag || !name) return;
  _safe(() => { window.gtag("event", name, params); });
};
